import React, { useMemo, useState } from "react";
import { FlexColumn, FlexRow } from "@/ui/FlexContainer/FlexContainer";
import Typography from "@/ui/Typography/Typography";
import { GlowingButton } from "@/ui/GlowingButton/GlowingButton";
import Input from "@/ui/Input/Input";
import { useUserDataContext } from "@/store/UserProvider";
import { cancelSubscription, getPaymentLink } from "@/api/payments";
import { isEmailValid } from "@/utils/helpers";
import {
  usePaymentLinkLastOpenStore,
  usePaymentStatusStore,
} from "@/store/paymentStatus";
import { useSubscriptionStore } from "@/store/useSubscriptionStore";
import ContainerLoader from "@/ui/ContainerLoader/ContainerLoader";
import Button from "@/ui/Button/Button";
import { ChangeCity } from "@/components/ChangeCity/ChangeCity";
import { setUserEmail } from "@/api/user";
import Offer from "@/components/Offer/Offer";
import { useLocation } from "react-router-dom";
import PremiumDescription from "@/components/PremiumDescription/PremiumDescription";
import { ChangePaymentOption } from "@/components/ChangePaymentOption/ChangePaymentOption";
import { usePaymentOptionStore } from "@/store/paymentOption";
import { ChoosePaymentOptions } from "@/components/ChangePaymentOption/ChoosePaymentOptions";
import { useTranslation } from "react-i18next";
import { useServiceType } from "@/utils/hooks/useServiceType";
import { CheckMarkIcon } from "@/assets/icons";
import cls from "@/features/PaymentsModal/styles.module.scss";

function PayButton({
  disabled,
  onClick,
  isHelper,
  email,
}: {
  disabled?: boolean;
  onClick?: () => void;
  isHelper?: boolean;
  email?: string;
}) {
  const { t } = useTranslation();
  const { user } = useUserDataContext();
  const [loading, setLoading] = useState(false);
  const { setShowedResult, setIsUpdating } = usePaymentStatusStore();
  const { setLastOpen } = usePaymentLinkLastOpenStore();

  const onSubmit = async () => {
    setLoading(true);

    try {
      if (email) await setUserEmail({ mail: email });
      const res = await getPaymentLink();
      setShowedResult(false);
      setIsUpdating(true);
      setLastOpen(new Date().toISOString());
      window.location.href = res;
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (isHelper)
    return (
      <Typography className={cls.openFail}>
        {t("paymentOptions.paymentPageNotOpen")}{" "}
        <button
          type="button"
          onClick={onClick || onSubmit}
          className={cls.link}
        >
          {t("common.repeat")}
        </button>
      </Typography>
    );

  return (
    <FlexColumn gap={16}>
      <GlowingButton
        onClick={onClick || onSubmit}
        disabled={disabled || loading}
        label={
          <FlexColumn gap={4}>
            <Typography variant="body1">{t("common.subscribe")}</Typography>
            <Typography variant="body2">
              {t("common.pricePerMonth", {
                price: user?.subscription_price,
              })}
            </Typography>
          </FlexColumn>
        }
      />
    </FlexColumn>
  );
}

export function PaymentError() {
  const { t } = useTranslation();
  const { isSravniDohod } = useServiceType();

  return (
    <FlexColumn gap={16}>
      <Typography className={cls.title}>
        {t("errorFallback.somethingWentWrong")}
      </Typography>
      <Typography className={cls.desc}>
        {t("paymentOptions.checkCard")}
      </Typography>
      <Typography className={cls.desc}>
        {t("errorFallback.contactUs")}
        <br />
        <a
          href={`https://t.me/${
            isSravniDohod ? "SravniDohodSupport" : "BigKefSupport"
          }`}
          className={cls.link}
        >
          {isSravniDohod ? "@SravniDohodSupport" : "@BigKefSupport"}
        </a>
      </Typography>
    </FlexColumn>
  );
}

interface SubscriptionFormProps {
  backBtn: () => void;
}

export function SubscriptionForm({ backBtn }: SubscriptionFormProps) {
  const { t } = useTranslation();
  const { isUpdating } = usePaymentStatusStore();
  const { isSubscriptionActive } = useSubscriptionStore();
  const { showedResult } = usePaymentStatusStore();
  const { user } = useUserDataContext();
  const [email, setEmail] = useState(user?.mail || "");
  const isValid = isEmailValid(email);

  return (
    <FlexColumn gap={92}>
      {!showedResult && !isSubscriptionActive ? (
        <FlexColumn alignItems="center" justifyContent="center">
          <ContainerLoader />
        </FlexColumn>
      ) : (
        <FlexColumn gap={32}>
          <FlexColumn gap={16}>
            <Typography className={cls.title}>{t("common.joinUs")}</Typography>
            <Typography className={cls.desc}>{t("common.check")}</Typography>
          </FlexColumn>
          <Input
            value={email}
            onChange={setEmail}
            label="Email"
            placeholder={t("common.emailForCheck")}
            error={
              email && !isEmailValid(email) ? t("common.incorrectEmail") : ""
            }
          />
        </FlexColumn>
      )}
      <FlexColumn gap={16}>
        <FlexColumn gap={8}>
          <PayButton email={email} disabled={!isValid || isUpdating} />
          <Offer />
        </FlexColumn>
        <button type="button" onClick={backBtn} className={cls.backBtnInForm}>
          {t("common.back")}
        </button>
      </FlexColumn>
    </FlexColumn>
  );
}

export default function BlockPayButton({
  disabled,
  onClick,
}: {
  disabled?: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useUserDataContext();

  return (
    <FlexColumn gap={10} alignItems="center" className={cls.btnBlock}>
      <Typography variant="body2" className={cls.priceText}>
        <s>399₽</s>{" "}
        {t("common.pricePerMonth", { price: user?.subscription_price })}
      </Typography>
      <GlowingButton
        onClick={onClick}
        disabled={disabled}
        label={
          <Typography className={cls.payBtnLabel}>
            {t("common.subscribe")}
          </Typography>
        }
      />
    </FlexColumn>
  );
}

export function PaymentSuccess() {
  const { t } = useTranslation();

  return (
    <FlexColumn gap={16}>
      <Typography className={cls.title}>
        {t("paymentOptions.paymentSuccessful")}
      </Typography>
      <Typography className={cls.desc}>{t("common.checkSent")}</Typography>
      <Typography className={cls.wide}>
        {t("common.subscriptionActive")}
      </Typography>
      <PremiumDescription />
    </FlexColumn>
  );
}

export function CancellationConfirm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { user } = useUserDataContext();
  const { setIsSubscriptionCancelled } = useSubscriptionStore();
  const { isSravniDohod } = useServiceType();

  const onCancel = async () => {
    if (!user) return;

    setLoading(true);

    try {
      await cancelSubscription(user.telegram_id);
      setIsSubscriptionCancelled(true);
      onClose();
    } catch (e) {
      //
    } finally {
      setLoading(false);
    }
  };

  return (
    <FlexColumn gap={16}>
      <Typography className={cls.wide}>{t("common.areYouSure")}</Typography>
      <Typography className={cls.desc}>
        {t("errorFallback.contactUs")}
        <br />
        <a
          href={`https://t.me/${
            isSravniDohod ? "SravniDohodSupport" : "BigKefSupport"
          }`}
          className={cls.link}
        >
          {isSravniDohod ? "@SravniDohodSupport" : "@BigKefSupport"}
        </a>
      </Typography>
      <FlexColumn gap={4}>
        <Button onClick={onClose} label={t("common.wantToUse")} />
        <Button
          variant="outlined"
          onClick={onCancel}
          label={t("common.yesShure")}
        />
      </FlexColumn>
    </FlexColumn>
  );
}

export function SubscriptionActive() {
  const { t } = useTranslation();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const { currentStep, goToStep } = usePaymentOptionStore();

  const onSubscriptionCancel = () => {
    setShowCancelConfirm(true);
  };

  if (showCancelConfirm) {
    return <CancellationConfirm onClose={() => setShowCancelConfirm(false)} />;
  }
  return (
    <>
      {currentStep === "firstPage" && (
        <FlexColumn gap={16}>
          <Typography className={cls.wide}>
            {t("common.subscriptionActive")}
          </Typography>

          <PremiumDescription onSubscriptionCancel={onSubscriptionCancel} />

          <ChangeCity />
          <ChangePaymentOption onChange={() => goToStep("secondPage")} />
        </FlexColumn>
      )}

      {currentStep === "secondPage" && <ChoosePaymentOptions />}
    </>
  );
}

export function ActiveCancelledSubscription() {
  const { t } = useTranslation();
  const { user } = useUserDataContext();
  const { currentStep, goToStep } = usePaymentOptionStore();

  const tillDate = user?.subscription_ends_at || "";

  return (
    <>
      {currentStep === "firstPage" && (
        <FlexColumn gap={16}>
          <FlexColumn gap={16}>
            <Typography className={cls.wide}>
              {t("common.tariffIsValidUntil")}
              <br />
              {new Date(tillDate).toLocaleDateString()}
            </Typography>
          </FlexColumn>
          <PremiumDescription />
          <ChangeCity />
          <ChangePaymentOption onChange={() => goToStep("secondPage")} />
        </FlexColumn>
      )}
      {currentStep === "secondPage" && <ChoosePaymentOptions />}
    </>
  );
}

export const FreeSubscribe = () => {
  const { t } = useTranslation();
  const { isSravniDohod } = useServiceType();
  const text = [
    "Просмотр текущих доходов в час в сервисе Яндекс Еда",
    "Просмотр текущих доходов в час в сервисе Купер",
    "Просмотр текущих коэффициентов Такси для категорий Эконом, Комфорт и Комфорт+",
    "Построение маршрута до мест повышенного коэффициента",
  ];

  return (
    <FlexColumn gap={16}>
      <Typography className={cls.wide}>
        {t("common.advantageousPoints")}
      </Typography>

      <FlexColumn>
        {text.map((elem) => (
          <FlexRow
            gap={4}
            alignItems="flex-start"
            className={
              isSravniDohod ? cls.freeSubscribeSrd : cls.freeSubscribeKef
            }
          >
            <CheckMarkIcon />
            <Typography>{elem}</Typography>
          </FlexRow>
        ))}
      </FlexColumn>

      <ChangeCity />
    </FlexColumn>
  );
};

export const PaymentModalContent = () => {
  const { showedResult } = usePaymentStatusStore();
  const { search } = useLocation();
  const { user } = useUserDataContext();
  const {
    isSubscriptionActive,
    isSubscriptionCancelled,
  } = useSubscriptionStore();

  const isPaymentConfirmed = useMemo(() => {
    return (
      new URLSearchParams(search).get("tgWebAppStartParam") ===
      "payment_confirmed_true"
    );
  }, [search]);

  if (isPaymentConfirmed && isSubscriptionActive) return <PaymentSuccess />;

  if (user?.selected_activate_option === "free_toggle" && isSubscriptionActive)
    return <FreeSubscribe />;

  if (isSubscriptionCancelled && isSubscriptionActive)
    return <ActiveCancelledSubscription />;

  if (showedResult && isSubscriptionActive) return <SubscriptionActive />;
};
